/* eslint react-hooks/exhaustive-deps: "warn" */
import { useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './TransferPricingSourcing.module.scss';

import { type PipelineStep } from './TransferPricingSourcing.types';

type Translate = (key: string) => string;

const getSteps = (t: Translate): PipelineStep[] => [
  {
    title: t('Collect from the registry'),
    description: t("Direct pipelines into the filing systems, on each registry's own publication cadence."),
    bullets: [
      t('Daily pushes, weekly batches, or a year of filings released in a single drop'),
      t('Whatever format the jurisdiction issues, in its own language and convention'),
      t("No intermediary re-selling somebody else's normalised copy"),
    ],
  },
  {
    title: t('Read the document'),
    description: t('Format classification first, then OCR wherever the filing is a scan.'),
    bullets: [
      t('Layout analysis recovers the table structure a plain text dump destroys'),
      t('Columns carry meaning in continental accounts, and losing the column loses the number'),
      t('Extraction is reviewed on exception rather than assumed correct'),
    ],
  },
  {
    title: t('Extract and map the line items'),
    description: t('Local caption to common field, without losing what the original said.'),
    bullets: [
      t('German Umsatzerlöse and Italian ricavi delle vendite resolve to the same field'),
      t('Mapping is held per jurisdiction and per accounting convention'),
      t('A Polish abridged filing and a German full filing resolve consistently'),
    ],
  },
  {
    title: t('Keep the document on the record'),
    description: t('Every figure carries the filing, the page, the date and the registry reference.'),
    bullets: [
      t('Standardisation sits on top of the source rather than in place of it'),
      t('A question about one line resolves to a document, not a methodology note'),
      t('Anything modelled is labelled as modelled, on the record itself'),
    ],
  },
];

const ScanVisual = () => (
  <div className={styles.scanCard}>
    <div className={styles.scanStamp} aria-hidden="true">
      <span>Amtsgericht</span>
      <span>Charlottenburg</span>
    </div>

    <div className={styles.scanHeader}>
      <b>Nordwaren Handels GmbH</b>
      <span>Berlin · HRB 214880 B</span>
    </div>

    <div className={styles.scanTitle}>Jahresabschluss</div>
    <div className={styles.scanSubtitle}>zum 31. Dezember 2025</div>

    <div className={styles.scanLines}>
      {[78, 62, 88, 66, 70, 60, 74, 58].map((width, index) => (
        <span key={index} className={styles.scanLine} style={{ width: `${width}%` }} />
      ))}
    </div>
  </div>
);

const OcrVisual = () => (
  <div className={styles.ocrCard}>
    {[
      { caption: 'Umsatzerlöse', value: '41.208' },
      { caption: 'Rohergebnis', value: '9.874' },
      { caption: 'Betriebsergebnis', value: '1.634' },
      { caption: 'Personalaufwand', value: '5.912' },
    ].map((row) => (
      <div key={row.caption} className={styles.ocrRow}>
        <span className={styles.ocrCaption}>{row.caption}</span>
        <span className={styles.ocrValue}>{row.value}</span>
      </div>
    ))}
  </div>
);

const MapVisual = () => (
  <div className={styles.mapCard}>
    <div className={styles.mapFiling}>
      <span className={styles.mapFlag}>DE</span>
      <div className={styles.mapFilingBody}>
        <span className={styles.mapFilingCaption}>Umsatzerlöse</span>
        <span className={styles.mapFilingValue}>41.208</span>
      </div>
    </div>

    <div className={styles.mapFiling}>
      <span className={styles.mapFlag}>IT</span>
      <div className={styles.mapFilingBody}>
        <span className={styles.mapFilingCaption}>Ricavi delle vendite</span>
        <span className={styles.mapFilingValue}>39.842</span>
      </div>
    </div>

    <div className={styles.mapConnector} aria-hidden="true">
      <span />
      <span />
    </div>

    <div className={styles.mapField}>turnover</div>
  </div>
);

const CiteVisual = () => (
  <div className={styles.citeCard}>
    <div className={styles.citeThumb}>
      <span className={styles.citeThumbLine} />
      <span className={styles.citeThumbLine} />
      <span className={cn(styles.citeThumbLine, styles.citeThumbLineHit)} />
      <span className={styles.citeThumbLine} />
      <span className={styles.citeThumbLine} />
    </div>

    <div className={styles.citeArrow} aria-hidden="true" />

    <div className={styles.citeCallout}>
      <span className={styles.citeTag}>p.4 · line 1</span>
      <div className={styles.citeRow}>
        <span>1. Umsatzerlöse</span>
        <b>41.208</b>
      </div>
    </div>
  </div>
);

const PANELS = [
  {
    heading: 'Inbound filing',
    visual: <ScanVisual />,
    caption: 'page 1 of 11 · 2.4 MB · 300 dpi',
    meta: [
      { key: 'Format', value: 'Image-only scan', note: 'No text layer, no structured tags', warn: true },
      { key: 'Language', value: 'German' },
      { key: 'Convention', value: 'Local GAAP (HGB)' },
    ],
    note: (
      <>
        Nothing structured is supplied by the registry. <b>What arrives is a picture of a signed document.</b>
      </>
    ),
  },
  {
    heading: 'OCR · page 4, Gewinn- und Verlustrechnung',
    visual: <OcrVisual />,
    caption: 'page 4 · 7 captions, 14 figures · all pairs matched · mean confidence 0.98',
    meta: [],
    note: (
      <>
        Layout analysis holds the caption and its figure together. <b>A plain text dump loses the pairing.</b>
      </>
    ),
  },
  {
    heading: 'Mapped fields',
    visual: <MapVisual />,
    caption: 'Two filings, two languages, two number formats. The caption is kept as filed on both.',
    meta: [
      { key: 'Rohergebnis', value: 'gross_profit' },
      { key: 'Betriebsergebnis', value: 'operating_result' },
      { key: 'Employees', value: '104', note: 'modelled — not disclosed in either filing', warn: true },
    ],
    note: null,
  },
  {
    heading: 'Field provenance',
    visual: <CiteVisual />,
    caption: '€41,208,000 · Jahresabschluss · page 4, line 1',
    meta: [
      { key: 'Filed', value: '28 Apr 2026' },
      { key: 'Registry reference', value: 'HRB 214880 B' },
      { key: 'Extraction', value: 'OCR · reviewed' },
    ],
    note: (
      <>
        The document stays attached.{' '}
        <b>An examiner&rsquo;s question about this figure has a page reference as its answer.</b>
      </>
    ),
  },
];

export const TransferPricingSourcing = () => {
  const { t } = useInt();
  const steps = getSteps(t);
  const [active, setActive] = useState(0);
  const stepRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const panelRef = useRef<HTMLDivElement>(null);
  const tickingRef = useRef(false);
  const panel = PANELS[active];

  useEffect(() => {
    const recalc = () => {
      tickingRef.current = false;

      const panel = panelRef.current;

      if (!panel) {
        return;
      }

      const ref = panel.getBoundingClientRect().top;
      let best = 0;
      let bestDistance = Infinity;

      stepRefs.current.forEach((step, index) => {
        if (!step) {
          return;
        }

        const distance = Math.abs(step.getBoundingClientRect().top - ref);

        if (distance < bestDistance) {
          bestDistance = distance;
          best = index;
        }
      });

      setActive(best);
    };

    const onScroll = () => {
      if (tickingRef.current) {
        return;
      }

      tickingRef.current = true;
      requestAnimationFrame(recalc);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  return (
    <Section background="#F6F7F9" className={styles.section}>
      <div className={styles.head}>
        <TitleBlock
          align="left"
          className={styles.titleBlock}
          title={t('Registries publish what they publish.')}
          description={
            <>
              {t(
                'Some give structured filings. Many give a PDF. A large share of the world still gives a scan of a signed document, in the local language and the local accounting convention.',
              )}
              <br />
              <br />
              {t('We collect those filings ourselves and digitise them in house, which is why')}{' '}
              <b>{t('the comparables you can reach depend on who actually did the collecting')}</b>.
            </>
          }
        />
      </div>

      <div className={styles.pipe}>
        <div className={styles.steps}>
          {steps.map((step, index) => (
            <button
              type="button"
              key={step.title}
              className={cn(styles.step, active === index && styles.stepOn)}
              ref={(el) => {
                stepRefs.current[index] = el;
              }}
              onClick={() => setActive(index)}
            >
              <span className={styles.stepNum}>{String(index + 1).padStart(2, '0')}</span>
              <h3 className={styles.stepTitle}>{step.title}</h3>
              <p className={styles.stepDescription}>{step.description}</p>

              <ul className={styles.stepList}>
                {step.bullets.map((bullet) => (
                  <li key={bullet}>{bullet}</li>
                ))}
              </ul>
            </button>
          ))}
        </div>

        <div className={styles.stick}>
          <div className={styles.card} ref={panelRef}>
            <div key={panel.heading} className={styles.panel}>
              <div className={styles.panelHead}>{panel.heading}</div>

              <div className={styles.panelBody}>
                {panel.visual}

                <div className={styles.panelCaption}>{panel.caption}</div>

                {panel.meta.length > 0 && (
                  <div className={styles.metaList}>
                    {panel.meta.map((row) => (
                      <div key={row.key} className={cn(styles.metaRow, row.warn && styles.metaRowWarn)}>
                        <span className={styles.metaKey}>{row.key}</span>
                        <span className={styles.metaValue}>
                          {row.value}
                          {row.note && <small>{row.note}</small>}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {panel.note && <div className={styles.panelNote}>{panel.note}</div>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
};
